import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Arrowdown from "../../../assets/Card/fi_chevron-down.png";
import { addUserStyles } from "../../UI/styles/Main";
import {
  useAddAdminMutation,
  useAddUserMutation,
  useAddDriverMutation,
  useGetAllOrganizationsQuery,
  useGetUserInfoQuery,
} from "../../../Api/apiSlice";
import CustomAlert from "../../UI/CustomAlert";
import { useAuth } from "../../../Authentication/AuthContext";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const validationSchema = (userRole) =>
  Yup.object({
    userName: Yup.string().required("Name is required"),
    userEmail: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    userPassword: Yup.string().required("Password is required"),
    userPhone: Yup.string()
      .matches(/^\d+$/, "Phone number must be digits only")
      .min(10, "Phone number should be at least 10 digits")
      .required("Phone number is required"),
    role: Yup.string()
      .oneOf(["admin", "customer", "driver", "superAdmin"], "Invalid role")
      .required("Role is required"),
    status: Yup.string().required("Status is required"),
    organizationId: Yup.string().test(
      "organization-required",
      "Organization is required",
      function (value) {
        if (userRole === "owner") {
          return !!value; // Require value if user is owner
        }
        return true; // Not required otherwise
      }
    ),
    orgType: Yup.string().when('role', {
      is: 'superAdmin',
      then: (schema) => schema.required('Please select organization type'),
      otherwise: (schema) => schema.notRequired(),
    }),
    orgName: Yup.string().when(['role', 'orgType'], {
      is: (role, orgType) => role === 'superAdmin' && orgType === 'new',
      then: (schema) => schema.required('Organization name is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    orgAddress: Yup.string().when(['role', 'orgType'], {
      is: (role, orgType) => role === 'superAdmin' && orgType === 'new',
      then: (schema) => schema.required('Organization address is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

function AddUser() {
  const [addAdmin] = useAddAdminMutation();
  const [addUser] = useAddUserMutation();
  const [addDriver] = useAddDriverMutation();
  const [showPassword, setShowPassword] = useState(false);

  const {userRole} = useAuth();

  const {
    data: organizations,
    error: orgError,
    isLoading: orgLoading,
  } = useGetAllOrganizationsQuery();

  const [alert, setAlert] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const handleAlertClose = () => {
    setAlert({ ...alert, open: false });
  };

  const handleRegister = async (values, { resetForm }) => {
    const { userName, userEmail, userPassword, userPhone, role, status, organizationId, orgName, orgAddress, orgType } =
      values;

    if (
      !userName ||
      !userEmail ||
      !userPassword ||
      !userPhone ||
      !role ||
      !status || 
      (userRole === "owner" && !organizationId) ||
      (role === "superAdmin" && !orgType) ||
      (role === "superAdmin" && orgType === "new" && (!orgName || !orgAddress))
    ) {
      setAlert({
        open: true,
        severity: "info",
        message: "Please fill out all fields.",
      });
      return;
    }

    const userData = {
      userName,
      userEmail,
      userPassword,
      userPhone,
      role,
      status,
      organizationId,
      ...(role === "superAdmin" && orgType === "new" && { orgName, orgAddress })
    };
    console.log("User data: ", userData);

    try {
      const mutation =
        role === "admin" ? addAdmin : role === "driver" ? addDriver : addUser;
      await mutation(userData).unwrap();

      setAlert({
        open: true,
        severity: "success",
        message: "User added successfully!",
      });
      resetForm();
    } catch (err) {
      setAlert({
        open: true,
        severity: "error",
        message: "User not added. Please try again.",
      });
      console.error("Add User failed", err);
    }
  };

  if (orgLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6">Loading Information...</Typography>
      </Box>
    );
  }
  
  if (orgError) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="error">
          Failed to load data. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper sx={addUserStyles.mainContainerStyles}>
      <Formik
        initialValues={{
          userName: "",
          userEmail: "",
          userPassword: "",
          userPhone: "",
          role: "",
          status: "",
          organizationId: "",
          orgType: "",
          orgName: "",
          orgAddress: "",
        }}
        validationSchema={validationSchema(userRole)}
        onSubmit={handleRegister}
      >
        {({ values, handleChange, setFieldValue, touched, errors }) => (
          <Form>
            <Box sx={addUserStyles.container} pl={{ xs: 2, sm: 8 }}>
              {/* Name Field */}
              <Box sx={{ width: { xs: "100%", sm: "35%" } }}>
                <Typography
                  variant="subtitle1"
                  mt={3}
                  mb={1}
                  sx={{ fontWeight: 500 }}
                >
                  Name
                </Typography>
                <Field
                  as={TextField}
                  sx={addUserStyles.textFieldStyles}
                  name="userName"
                  label="Enter Name"
                  size="medium"
                  fullWidth
                  helperText={<ErrorMessage name="userName" />}
                  error={touched.userName && Boolean(errors.userName)}
                />
              </Box>

              {/* Email Field */}
              <Box sx={{ width: { xs: "100%", sm: "35%" } }}>
                <Typography
                  variant="subtitle1"
                  mt={3}
                  mb={1}
                  sx={{ fontWeight: 500 }}
                >
                  Email
                </Typography>
                <Field
                  as={TextField}
                  sx={addUserStyles.textFieldStyles}
                  name="userEmail"
                  label="Enter Email"
                  size="medium"
                  fullWidth
                  helperText={<ErrorMessage name="userEmail" />}
                  error={touched.userEmail && Boolean(errors.userEmail)}
                />
              </Box>

              {/* Password Field */}
              <Box sx={{ width: { xs: "100%", sm: "35%" } }}>
                <Typography
                  variant="subtitle1"
                  mt={3}
                  mb={1}
                  sx={{ fontWeight: 500 }}
                >
                  Password
                </Typography>
                <Field
                  as={TextField}
                  sx={addUserStyles.textFieldStyles}
                  name="userPassword"
                  label="Enter Password"
                  type={showPassword ? "text" : "password"}
                  size="medium"
                  fullWidth
                  helperText={<ErrorMessage name="userPassword" />}
                  error={touched.userPassword && Boolean(errors.userPassword)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Phone Number Field */}
              <Box sx={{ width: { xs: "100%", sm: "35%" } }}>
                <Typography
                  variant="subtitle1"
                  mt={3}
                  mb={1}
                  sx={{ fontWeight: 500 }}
                >
                  Phone Number
                </Typography>
                <Field
                  as={TextField}
                  sx={addUserStyles.textFieldStyles}
                  name="userPhone"
                  label="Enter Phone Number"
                  size="medium"
                  fullWidth
                  helperText={<ErrorMessage name="userPhone" />}
                  error={touched.userPhone && Boolean(errors.userPhone)}
                />
              </Box>

              {userRole === 'owner' && <Box sx={{ width: { xs: "92%", sm: "32%", lg: "35.5%" } }}>
                <Typography
                  variant="subtitle1"
                  mt={3}
                  mb={1}
                  sx={{ fontWeight: 500 }}
                >
                  Organization
                </Typography>
               <FormControl
                  fullWidth
                  size="medium"
                  disabled={userRole !== "owner"}
                >
                  <InputLabel>Organization</InputLabel>
                  <Field
                    as={Select}
                    name="organizationId"
                    value={values.organizationId}
                    onChange={(e) =>
                      setFieldValue("organizationId", e.target.value)
                    }
                    label="Organization"
                    IconComponent={() => (
                      <IconButton sx={{ padding: 0 }}>
                        <img src={Arrowdown} height="16px" width="20px" />
                      </IconButton>
                    )}
                  >
                    {orgLoading ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : orgError ? (
                      <MenuItem disabled>Error loading organizations</MenuItem>
                    ) : (
                      organizations?.map((org) => (
                        <MenuItem key={org.id} value={org.id}>
                          {org.name}
                        </MenuItem>
                      ))
                    )}
                  </Field>
                </FormControl>
              </Box>}

              {/* Role Field */}
              <Box sx={{ width: { xs: "92%", sm: "32%", lg: "35.5%" } }}>
                <Typography
                  variant="subtitle1"
                  mt={3}
                  mb={1}
                  sx={{ fontWeight: 500 }}
                >
                  Role
                </Typography>
                <FormControl fullWidth size="medium">
                  <InputLabel>Role</InputLabel>
                  <Field
                    as={Select}
                    name="role"
                    value={values.role}
                    onChange={(e) => setFieldValue("role", e.target.value)}
                    label="Role"
                    IconComponent={() => (
                      <IconButton sx={{ padding: 0 }}>
                        <img src={Arrowdown} height="16px" width="20px" />
                      </IconButton>
                    )}
                  >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="customer">Customer</MenuItem>
                    <MenuItem value="driver">Driver</MenuItem>
                    <MenuItem value="superAdmin">Super Admin</MenuItem>
                  </Field>
                </FormControl>
              </Box>

              {/* Organization Type Selection - Only show when superAdmin is selected */}
              {values.role === "superAdmin" && (
                <Box sx={{ width: { xs: "100%", sm: "35%" } }}>
                  <Typography
                    variant="subtitle1"
                    mt={3}
                    mb={1}
                    sx={{ fontWeight: 500 }}
                  >
                    Organization Type
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      name="orgType"
                      value={values.orgType}
                      onChange={(e) => setFieldValue("orgType", e.target.value)}
                    >
                      <FormControlLabel
                        value="same"
                        control={<Radio />}
                        label="Same Organization (use current organization)"
                      />
                      <FormControlLabel
                        value="new"
                        control={<Radio />}
                        label="New Organization (create new organization)"
                      />
                    </RadioGroup>
                  </FormControl>
                  {touched.orgType && errors.orgType && (
                    <Typography color="error" variant="caption">
                      {errors.orgType}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Organization Name Field - Only show when superAdmin + new organization is selected */}
              {values.role === "superAdmin" && values.orgType === "new" && (
                <Box sx={{ width: { xs: "100%", sm: "35%" } }}>
                  <Typography
                    variant="subtitle1"
                    mt={3}
                    mb={1}
                    sx={{ fontWeight: 500 }}
                  >
                    Organization Name
                  </Typography>
                  <Field
                    as={TextField}
                    sx={addUserStyles.textFieldStyles}
                    name="orgName"
                    label="Enter Organization Name"
                    size="medium"
                    fullWidth
                    helperText={<ErrorMessage name="orgName" />}
                    error={touched.orgName && Boolean(errors.orgName)}
                  />
                </Box>
              )}

              {/* Organization Address Field - Only show when superAdmin + new organization is selected */}
              {values.role === "superAdmin" && values.orgType === "new" && (
                <Box sx={{ width: { xs: "100%", sm: "35%" } }}>
                  <Typography
                    variant="subtitle1"
                    mt={3}
                    mb={1}
                    sx={{ fontWeight: 500 }}
                  >
                    Organization Address
                  </Typography>
                  <Field
                    as={TextField}
                    sx={addUserStyles.textFieldStyles}
                    name="orgAddress"
                    label="Enter Organization Address"
                    size="medium"
                    fullWidth
                    multiline
                    rows={3}
                    helperText={<ErrorMessage name="orgAddress" />}
                    error={touched.orgAddress && Boolean(errors.orgAddress)}
                  />
                </Box>
              )}

              {/* Status Field */}
              <Box sx={{ width: { xs: "92%", sm: "32%", lg: "35.5%" } }}>
                <Typography
                  variant="subtitle1"
                  mt={3}
                  mb={1}
                  sx={{ fontWeight: 500 }}
                >
                  Status
                </Typography>
                <FormControl fullWidth size="medium">
                  <InputLabel>Status</InputLabel>
                  <Field
                    as={Select}
                    name="status"
                    value={values.status}
                    onChange={(e) => setFieldValue("status", e.target.value)}
                    label="Status"
                    IconComponent={() => (
                      <IconButton sx={{ padding: 0 }}>
                        <img src={Arrowdown} height="16px" width="20px" />
                      </IconButton>
                    )}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Field>
                </FormControl>
              </Box>

              {/* Add Button */}
              <Stack mt={6} sx={{ gap: { xs: "16px", sm: "24px" } }}>
                <Button
                  variant="contained"
                  sx={addUserStyles.buttonStyles}
                  type="submit"
                >
                  Add
                </Button>
                <CustomAlert
                  open={alert.open}
                  onClose={handleAlertClose}
                  severity={alert.severity}
                  message={alert.message}
                />
              </Stack>
            </Box>
          </Form>
        )}
      </Formik>
    </Paper>
  );
}

export default AddUser;
